/**
 * Campaign content kit — the ONE definition of "what actually sends", shared by
 * the server sender AND the browser preview so the two can never drift.
 *
 * PURE MODULE — no node-only deps, no I/O, no imports from `@borradh-workspace/
 * database`, `env`, `ai`, etc. This is what lets the frontend value-import it
 * (via `@borradh-workspace/features/campaigns/content`, re-exported by
 * `@borradh-workspace/api-client`) without dragging the node-heavy campaigns
 * barrel into the browser bundle.
 */
/**
 * The mandatory opt-out line that every WhatsApp campaign must carry. It is
 * part of the canonical template body below, and shown in the preview — it is a
 * hard compliance gate, never editable away.
 */
export const WHATSAPP_STOP_LINE = 'Reply STOP to unsubscribe.';
/**
 * The single WhatsApp template the everyday campaign flow standardises on.
 *
 *   Hi {{1}}, this is {{2}}.   ← {{1}} = recipient first name, {{2}} = the
 *                                sending org's name — both auto-bound, never
 *                                editable in the composer
 *
 *   {{3}}                      ← {{3}} is the one user-authored slot
 *
 *   Reply STOP to unsubscribe.   ← the hard-gated opt-out line
 *
 * Shared by the auto-provision step (`ensureCampaignWhatsappTemplate`) and the
 * composer so the name, language, category and STOP line have ONE source.
 *
 * The `{{2}}` business-name slot exists specifically because a generic,
 * unbounded, no-context template ("Hi {{1}}, {{2}}, STOP to unsubscribe") is a
 * known WhatsApp MARKETING-category rejection pattern — Meta's reviewers (and
 * their classifier) read a template with no static business context as an
 * open slot for arbitrary content. Naming the sender in the template body
 * itself, not just in the free-text message, is what a template needs to read
 * as one specific, reviewable message type rather than "anything, sent by
 * anyone." `CANONICAL_WHATSAPP_TEMPLATE_EXAMPLE` below supplies the example
 * values Meta requires per placeholder at submission time — omitting examples
 * is a separate, harder rejection cause `createWhatsappTemplate` now avoids.
 */
export const CANONICAL_WHATSAPP_TEMPLATE = {
    name: 'borradh_campaign_message',
    languageCode: 'en_US',
    category: 'MARKETING',
    body: `Hi {{1}}, this is {{2}}.\n\n{{3}}\n\n${WHATSAPP_STOP_LINE}`,
};
/**
 * Realistic example values for the canonical template's `{{1}}`, `{{2}}`,
 * `{{3}}` placeholders, in order — what Meta shows reviewers and requires at
 * submission. Not the literal merge-tag tokens the composer sends at runtime;
 * a plausible instance of each, the way a reviewer needs to see it.
 */
export const CANONICAL_WHATSAPP_TEMPLATE_EXAMPLE = [
    'Sarah',
    'Bloom Aesthetics',
    'Book your next appointment this week and save 20%!',
];
/** Escape the HTML-significant characters so authored text can't inject markup. */
function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/**
 * Convert a PLAIN-TEXT campaign email body (authored in a textarea) into the
 * exact HTML the email carries. Blank-line-separated blocks become `<p>`s;
 * single newlines become `<br/>`; markup is escaped. When `unsubscribeUrl` is
 * provided, the exact `<hr>` + "Don't want these emails? Unsubscribe" footer
 * the sender injects is appended.
 *
 * This is the ONE converter both the sender and the preview import, so the
 * preview is byte-for-byte what gets delivered.
 */
export function renderCampaignEmailHtml(body, opts) {
    const blocks = body
        .replace(/\r\n/g, '\n')
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter((block) => block.length > 0)
        .map((block) => `<p style="margin:0 0 16px;line-height:1.5">${escapeHtml(block).replace(/\n/g, '<br/>')}</p>`);
    // A body with no text still needs a container so the footer renders cleanly.
    const bodyHtml = blocks.join('') || '<p style="margin:0"></p>';
    if (!opts?.unsubscribeUrl)
        return bodyHtml;
    return `${bodyHtml}<hr style="margin-top:32px;border:none;border-top:1px solid #eee"/><p style="font-size:12px;color:#888;text-align:center">Don't want these emails? <a href="${opts.unsubscribeUrl}" style="color:#888">Unsubscribe</a></p>`;
}
/** Extract the unique, ascending `{{n}}` positional-param indices from a body. */
export function extractParamIndices(body) {
    const indices = new Set();
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    let match = re.exec(body);
    while (match !== null) {
        indices.add(Number(match[1]));
        match = re.exec(body);
    }
    return [...indices].sort((a, b) => a - b);
}
/**
 * Pick the campaign WhatsApp template and split its params into the auto-bound
 * first-name param (`{{1}}`) and the user-fillable rest (`{{2}}..{{n}}`).
 *
 * Only `borradh_campaign_message` is eligible for WhatsApp campaigns.
 */
export function resolveCampaignWhatsappTemplate(templates) {
    const chosen = templates.find((t) => t.name === CANONICAL_WHATSAPP_TEMPLATE.name);
    if (!chosen)
        return null;
    const paramIndices = extractParamIndices(chosen.body);
    const firstNameParamIndex = chosen.name === CANONICAL_WHATSAPP_TEMPLATE.name && paramIndices.includes(1)
        ? 1
        : null;
    // `{{2}}` is the business-name slot ONLY on a template whose approved body
    // is actually the current canonical one. Matching on the name alone would
    // mis-read an org still holding the PREVIOUS two-param canonical template
    // (`Hi {{1}},\n\n{{2}}\n\n<stop>`, where `{{2}}` is the authored message):
    // its `{{2}}` would be claimed as an auto-param, `editableParamIndices`
    // would come back empty, and the operator's message would be silently
    // replaced by the org name on every send. Those templates are already
    // approved on Meta and `ensureCampaignWhatsappTemplate` is a name-keyed
    // no-op, so they persist indefinitely — they must keep their old meaning.
    const isCanonical = chosen.name === CANONICAL_WHATSAPP_TEMPLATE.name &&
        chosen.body === CANONICAL_WHATSAPP_TEMPLATE.body;
    const businessNameParamIndex = isCanonical && paramIndices.includes(2) ? 2 : null;
    const autoIndices = new Set([firstNameParamIndex, businessNameParamIndex].filter((i) => i != null));
    const editableParamIndices = paramIndices.filter((i) => !autoIndices.has(i));
    return {
        template: chosen,
        firstNameParamIndex,
        businessNameParamIndex,
        editableParamIndices,
    };
}
/**
 * One WhatsApp template parameter exactly as Meta receives it. Meta rejects
 * line breaks, tabs and runs of spaces inside a template variable (its static
 * body may have them), so the sender flattens every interpolated parameter
 * with this, and `fillWhatsappTemplate` does the same for the preview.
 */
export function normalizeWhatsappTemplateParam(value) {
    return value.replace(/\s+/g, ' ').trim();
}
/**
 * Fill a template body's `{{n}}` params from an index-keyed record, each value
 * normalized as the sender sends it. Params with no supplied value keep their
 * `{{n}}` token (so the preview shows what's still unfilled). Literal text —
 * including the STOP line — is left untouched.
 */
export function fillWhatsappTemplate(body, valuesByParamIndex) {
    return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (token, digits) => {
        const index = Number(digits);
        return index in valuesByParamIndex
            ? normalizeWhatsappTemplateParam(valuesByParamIndex[index])
            : token;
    });
}
//# sourceMappingURL=campaign-content.js.map